import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, TextField, FormControlLabel,
  Checkbox, Alert, Paper, LinearProgress, Fade
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { fetchKioskConfig, submitKioskCheckin, hasKioskSession } from './api';

const theme = createTheme({
  palette: { mode: 'light', primary: { main: '#1976d2' } },
  typography: { fontSize: 18 },
  components: {
    MuiButton: { styleOverrides: { root: { minHeight: 56, fontSize: '1.1rem' } } },
    MuiTextField: { styleOverrides: { root: { '& input': { fontSize: '1.1rem' } } } },
  },
});

const STEPS = { LOADING: 0, WELCOME: 1, FORM: 2, REASON: 3, SUBREASON: 4, CONFIRM: 5, DONE: 6, ERROR: 7 };
const INACTIVITY_MS = 60 * 1000; // reset after 60s of no interaction
const DONE_RESET_MS = 10 * 1000; // auto-reset 10s after confirmation screen

const generateKey = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const KioskApp = () => {
  const [step, setStep] = useState(STEPS.LOADING);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState(generateKey);
  const inactivityTimer = useRef(null);
  const doneTimer = useRef(null);

  const [form, setForm] = useState({
    ownerName: '', petName: '', phone: '', email: '',
    parkingSpace: '', vehicle: '', comment: '',
    reasonId: '', subreasonId: '', answers: {},
  });

  const resetForm = useCallback(() => {
    setForm({ ownerName: '', petName: '', phone: '', email: '', parkingSpace: '', vehicle: '', comment: '', reasonId: '', subreasonId: '', answers: {} });
    setError('');
    setIdempotencyKey(generateKey());
    setStep(STEPS.WELCOME);
  }, []);

  // Inactivity timer — reset to welcome on any step except LOADING/DONE/ERROR
  const resetInactivity = useCallback(() => {
    clearTimeout(inactivityTimer.current);
    if (step > STEPS.WELCOME && step < STEPS.DONE) {
      inactivityTimer.current = setTimeout(resetForm, INACTIVITY_MS);
    }
  }, [step, resetForm]);

  useEffect(() => {
    window.addEventListener('touchstart', resetInactivity);
    window.addEventListener('mousedown', resetInactivity);
    resetInactivity();
    return () => {
      window.removeEventListener('touchstart', resetInactivity);
      window.removeEventListener('mousedown', resetInactivity);
      clearTimeout(inactivityTimer.current);
    };
  }, [resetInactivity]);

  // Auto-reset after done screen
  useEffect(() => {
    if (step === STEPS.DONE) {
      doneTimer.current = setTimeout(resetForm, DONE_RESET_MS);
    }
    return () => clearTimeout(doneTimer.current);
  }, [step, resetForm]);

  // Load config — token already in sessionStorage from KioskRoute
  useEffect(() => {
    if (!hasKioskSession()) {
      setError('No kiosk session found. Please scan the setup URL again.');
      setStep(STEPS.ERROR);
      return;
    }
    fetchKioskConfig()
      .then(cfg => { setConfig(cfg); setStep(STEPS.WELCOME); })
      .catch(() => { setError('Unable to connect. Please ask staff for assistance.'); setStep(STEPS.ERROR); });
  }, []);

  // Block browser navigation
  useEffect(() => {
    const block = e => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', block);
    return () => window.removeEventListener('beforeunload', block);
  }, []);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const setAnswer = (qId, value) => setForm(f => ({ ...f, answers: { ...f.answers, [qId]: value } }));

  const activeQuestions = config?.questions || [];
  const selectedReason = config?.reasons?.find(r => r.id === form.reasonId);
  const subreasons = selectedReason?.subreasons || [];

  const handleFormNext = () => {
    if (!form.ownerName.trim() || !form.petName.trim()) {
      setError('Owner name and pet name are required.');
      return;
    }
    setError('');
    if (config?.reasons?.length) setStep(STEPS.REASON);
    else setStep(STEPS.CONFIRM);
  };

  const handleReasonNext = () => {
    if (subreasons.length) setStep(STEPS.SUBREASON);
    else setStep(STEPS.CONFIRM);
  };

  const handleSubmit = async () => {
    setStep(STEPS.LOADING);
    try {
      await submitKioskCheckin({ ...form, idempotencyKey });
      setStep(STEPS.DONE);
    } catch (e) {
      setError(e.response?.data?.errors || 'Check-in failed. Please ask staff for help.');
      setStep(STEPS.ERROR);
    }
  };

  const siteTitle = config?.siteTitle || 'Check In';

  return (
    <ThemeProvider theme={theme}>
      {/* Full-screen, no scrollbar, no address bar interaction */}
      <Box
        sx={{ minHeight: '100vh', bgcolor: '#f0f4f8', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, userSelect: 'none' }}
        onContextMenu={e => e.preventDefault()}
      >
        <Paper elevation={4} sx={{ width: '100%', maxWidth: 560, p: 4, borderRadius: 3 }}>

          {/* Loading */}
          {step === STEPS.LOADING && <LinearProgress />}

          {/* Welcome */}
          {step === STEPS.WELCOME && (
            <Fade in>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" fontWeight={600} gutterBottom>{siteTitle}</Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                  Tap below to check in your pet
                </Typography>
                <Button variant="contained" size="large" fullWidth sx={{ py: 3, fontSize: '1.3rem' }}
                  onClick={() => setStep(STEPS.FORM)}>
                  Check In
                </Button>
                {config?.hoursNote && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>{config.hoursNote}</Typography>
                )}
              </Box>
            </Fade>
          )}

          {/* Form */}
          {step === STEPS.FORM && (
            <Fade in>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" gutterBottom>Your information</Typography>
                {error && <Alert severity="error">{error}</Alert>}
                <TextField label="Your Name" required fullWidth value={form.ownerName} onChange={e => set('ownerName', e.target.value)} inputProps={{ autoCapitalize: 'words' }} />
                <TextField label="Pet Name" required fullWidth value={form.petName} onChange={e => set('petName', e.target.value)} inputProps={{ autoCapitalize: 'words' }} />
                <TextField label="Phone Number" fullWidth value={form.phone} onChange={e => set('phone', e.target.value)} inputProps={{ inputMode: 'tel' }} />
                <TextField label="Email" fullWidth value={form.email} onChange={e => set('email', e.target.value)} inputProps={{ inputMode: 'email' }} />
                <TextField label="Parking Space #" fullWidth value={form.parkingSpace} onChange={e => set('parkingSpace', e.target.value)} />
                <TextField label="Vehicle Description" fullWidth value={form.vehicle} onChange={e => set('vehicle', e.target.value)} />
                {activeQuestions.map(q => (
                  q.type === 'yes_no' ? (
                    <FormControlLabel key={q.id}
                      control={<Checkbox checked={!!form.answers[q.id]} onChange={e => setAnswer(q.id, e.target.checked)} />}
                      label={q.label + (q.required ? ' *' : '')}
                    />
                  ) : (
                    <TextField key={q.id} label={q.label} required={q.required} fullWidth
                      multiline={q.type === 'long_text'} rows={q.type === 'long_text' ? 3 : 1}
                      value={form.answers[q.id] || ''}
                      onChange={e => setAnswer(q.id, e.target.value)}
                    />
                  )
                ))}
                <TextField label="Comments" fullWidth multiline rows={2} value={form.comment} onChange={e => set('comment', e.target.value)} />
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Button fullWidth variant="outlined" onClick={resetForm}>Cancel</Button>
                  <Button fullWidth variant="contained" size="large" onClick={handleFormNext}>Next</Button>
                </Box>
              </Box>
            </Fade>
          )}

          {/* Reason */}
          {step === STEPS.REASON && (
            <Fade in>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" gutterBottom>Reason for visit</Typography>
                {config.reasons.map(r => (
                  <Button key={r.id}
                    variant={form.reasonId === r.id ? 'contained' : 'outlined'}
                    fullWidth sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 2 }}
                    onClick={() => { set('reasonId', r.id); set('subreasonId', ''); }}>
                    {r.label}
                  </Button>
                ))}
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Button fullWidth variant="outlined" onClick={() => setStep(STEPS.FORM)}>Back</Button>
                  <Button fullWidth variant="contained" size="large" disabled={!form.reasonId} onClick={handleReasonNext}>Next</Button>
                </Box>
              </Box>
            </Fade>
          )}

          {/* Subreason */}
          {step === STEPS.SUBREASON && (
            <Fade in>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" gutterBottom>{selectedReason?.label} — select a category</Typography>
                {subreasons.map(s => (
                  <Button key={s.id}
                    variant={form.subreasonId === s.id ? 'contained' : 'outlined'}
                    fullWidth sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 2 }}
                    onClick={() => set('subreasonId', s.id)}>
                    {s.label}
                  </Button>
                ))}
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <Button fullWidth variant="outlined" onClick={() => setStep(STEPS.REASON)}>Back</Button>
                  <Button fullWidth variant="contained" size="large" disabled={!form.subreasonId} onClick={() => setStep(STEPS.CONFIRM)}>Next</Button>
                </Box>
              </Box>
            </Fade>
          )}

          {/* Confirm */}
          {step === STEPS.CONFIRM && (
            <Fade in>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="h6" gutterBottom>Confirm your details</Typography>
                <Box sx={{ bgcolor: '#f9f9f9', borderRadius: 2, p: 2.5 }}>
                  <Typography><strong>Name:</strong> {form.ownerName}</Typography>
                  <Typography><strong>Pet:</strong> {form.petName}</Typography>
                  {form.phone && <Typography><strong>Phone:</strong> {form.phone}</Typography>}
                  {form.parkingSpace && <Typography><strong>Parking Space:</strong> {form.parkingSpace}</Typography>}
                  {form.reasonId && (
                    <Typography>
                      <strong>Reason:</strong> {selectedReason?.label}
                      {form.subreasonId && ` — ${subreasons.find(s => s.id === form.subreasonId)?.label}`}
                    </Typography>
                  )}
                  {form.comment && <Typography><strong>Comment:</strong> {form.comment}</Typography>}
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button fullWidth variant="outlined" onClick={() => setStep(config?.reasons?.length ? STEPS.REASON : STEPS.FORM)}>Back</Button>
                  <Button fullWidth variant="contained" size="large" onClick={handleSubmit}>Check In</Button>
                </Box>
              </Box>
            </Fade>
          )}

          {/* Done */}
          {step === STEPS.DONE && (
            <Fade in>
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h4" gutterBottom>✓ You're checked in!</Typography>
                <Typography variant="h6" color="text.secondary">
                  Please take a seat. A staff member will be with you shortly.
                </Typography>
                {config?.hoursNote && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>{config.hoursNote}</Typography>
                )}
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 3 }}>
                  This screen will reset automatically.
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Error */}
          {step === STEPS.ERROR && (
            <Box sx={{ textAlign: 'center' }}>
              <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please speak with a staff member for assistance.
              </Typography>
              <Button variant="outlined" onClick={resetForm}>Try Again</Button>
            </Box>
          )}

        </Paper>
      </Box>
    </ThemeProvider>
  );
};

export default KioskApp;
